import { css, useTheme } from '@emotion/react';

import { spacing } from '@/theme/tokens';

export interface SectionDisplayProps {
  title: string | null;
  content: string;
  position: number;
  isCurrent: boolean;
  showDivider: boolean;
  onClick: () => void;
  fontSize?: number;
  speakerNotes?: string[];
}

export function SectionDisplay({
  title,
  content,
  position,
  isCurrent,
  showDivider,
  onClick,
  fontSize = 48,
  speakerNotes,
}: SectionDisplayProps) {
  const theme = useTheme();

  return (
    <>
      {showDivider && (
        <hr
          css={css`
            border: none;
            border-top: 1px solid ${theme.colors.border};
            margin: ${spacing.xl} 0;
          `}
        />
      )}
      <div
        data-section={position}
        data-current={isCurrent || undefined}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        css={css`
          padding: ${spacing.lg};
          border-left: 4px solid ${isCurrent ? theme.colors.primary : 'transparent'};
          background: ${isCurrent ? `${theme.colors.primary}10` : 'transparent'};
          border-radius: 4px;
          cursor: pointer;
          transition:
            border-color 0.2s ease,
            background 0.2s ease;
        `}
      >
        <h2
          css={css`
            margin: 0 0 ${spacing.md};
            font-size: ${fontSize}px;
            color: ${theme.colors.primary};
            font-weight: 600;
          `}
        >
          {title || `Section ${position + 1}`}
        </h2>
        <p
          css={css`
            margin: 0;
            font-size: ${fontSize}px;
            color: ${theme.colors.text};
            white-space: pre-wrap;
            line-height: 1.4;
          `}
        >
          {content}
        </p>
        {speakerNotes &&
          speakerNotes.length > 0 &&
          speakerNotes.map((note, i) => (
            <div
              key={i}
              data-speaker-note
              css={css`
                border-left: 3px solid ${theme.colors.primary};
                background: ${theme.colors.primary}10;
                padding: 6px 12px;
                margin-top: ${spacing.sm};
                border-radius: 0 4px 4px 0;
                font-style: italic;
                font-size: ${fontSize * 0.6}px;
                opacity: 0.7;
              `}
            >
              <span
                css={css`
                  font-weight: 600;
                  font-size: 0.75em;
                  text-transform: uppercase;
                  letter-spacing: 0.04em;
                  margin-right: 6px;
                  opacity: 0.8;
                `}
              >
                Note:
              </span>
              {note}
            </div>
          ))}
      </div>
    </>
  );
}
